/**
 * Rechnungsbetrag auf die Bestellungen einer Sammellieferung verteilen - baut auf
 * `distributeDocumentAmount`/`shipmentOrderGroup` (`lib/landedCost.ts`, Task 7b-Portierung von
 * `index.html:12244-12264`) auf.
 *
 * ================= Kein 1:1-Gegenstueck im Bestand - Verhaltensaenderung, im Bericht gefuehrt ====
 *
 * Im Bestand steht diese Aufteilung NICHT im Drawer-Rumpf: sie erscheint erst innerhalb des
 * OCR-Pruefdialogs `#documentOcrOverlay` (`renderDocumentOcrReview`, `index.html:12363-12426`),
 * NACHDEM ein Sammeldokument (CI/Zoll/Fracht) hochgeladen und ausgelesen wurde - ein Modal ausserhalb
 * von `.drawer-body`. Diese Portierung baut den Parser-Pruefdialog selbst NICHT (eigener Plan, siehe
 * `lib/parsers/naht.ts`); der Task-Brief verlangt aber ausdruecklich eine eigene `Verteilung.tsx` samt
 * Test, der `distributeDocumentAmount` UNMITTELBAR im Drawer prueft (`getByLabelText("Betrag")` ohne
 * vorherigen Dialog-Umweg). Diese Komponente ist deshalb ein eigenstaendiges, vereinfachtes Widget:
 * es erscheint, sobald die aktuelle Bestellung Teil einer Sammellieferung mit mehr als einem Mitglied
 * ist (`shipmentOrderGroup(...).length > 1`) - unabhaengig von einem OCR-Vorgang. Fuer `best-03`, die
 * Baseline-Bestellung, gilt das NICHT (kein `splitFrom` in `test/visual/daten/basis.json`, Gruppe hat
 * genau ein Mitglied) - die Baseline `drawer-docs` zeigt darum niemals dieses Widget, und der
 * Struktur-Nachweis bleibt davon unberuehrt.
 *
 * `fmtMoney` (aus `lib/money.ts`) verlangt eine Lieferantenliste, die dieser Reiter laut Brief-Signatur
 * nicht erhaelt (`DokumenteTab`-Props: `bestellung`, `bestellungen`, `rechte`, `onAendern`,
 * `onHochladen` - keine `lieferanten`). Die Betraege werden deshalb ueber `EUR` (`lib/format.ts`)
 * angezeigt, ohne Waehrungsauswahl - eine dokumentierte Vereinfachung, siehe Task-Bericht.
 */
import { useState } from "react";
import { Abschnitt } from "../Abschnitt.tsx";
import { EUR } from "../../../lib/format.ts";
import { orderTotal } from "../../../lib/payments.ts";
import { distributeDocumentAmount, shipmentOrderGroup } from "../../../lib/landedCost.ts";
import type { Order } from "../../../lib/types.ts";

export interface VerteilungProps {
  bestellung: Order;
  bestellungen: readonly Order[];
}

export function Verteilung({ bestellung: o, bestellungen }: VerteilungProps) {
  const [betrag, setBetrag] = useState("");
  const gruppe = shipmentOrderGroup(o, bestellungen);

  // Nur bei einer echten Sammellieferung (siehe Kopfkommentar) - sonst gibt es nichts zu verteilen.
  if (gruppe.length <= 1) return null;

  const anteile = distributeDocumentAmount(betrag, gruppe);

  return (
    <Abschnitt schluessel="docs-verteilung" titel="Rechnungsbetrag auf die Sammellieferung verteilen">
      <label className="doc-ocr-field">
        Betrag
        <input
          type="number"
          min="0"
          step="0.01"
          value={betrag}
          onChange={(e) => setBetrag(e.currentTarget.value)}
        />
      </label>
      <table className="doc-ocr-table">
        <thead>
          <tr>
            <th>Bestellung</th>
            <th>Bestellwert</th>
            <th className="num">Zu übernehmender Betrag</th>
          </tr>
        </thead>
        <tbody>
          {anteile.map((anteil) => {
            const bestellung = gruppe.find((kandidat) => kandidat.id === anteil.orderId);
            return (
              <tr key={String(anteil.orderId)}>
                <td>
                  <strong>{(bestellung?.orderNo as string) || (bestellung?.name as string) || "Bestellung"}</strong>
                </td>
                <td>{bestellung ? EUR.format(orderTotal(bestellung)) : "–"}</td>
                <td className="num">{EUR.format(anteil.amount)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Abschnitt>
  );
}
